import React from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Alert, Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from "@mui/material";
import { getErrorMessage } from "utils/errorHelpers";
import { FulfillmentStepKind, InternalConfirmationState } from "client/api-v2";
import type { OrderDto, OrderFulfillmentDto, OrderInternalConfirmationDto } from "client/api-v2";
import { detailFieldLabelSx } from "components/InfoField/InfoField";
import { BuilderSection } from "areas/orders/builder/BuilderSection";
import { isOrderLocked } from "areas/orders/builder/LockedOrderBanner";
import type { SectionProps } from "areas/orders/builder/sections/SectionProps";
import { formatInstantInUtc } from "areas/orders/components/OrderVersionHistory";
import { setInternalConfirmation } from "areas/orders/services/orderService";

/** Nothing at all fired. The approved design keeps the prototype's own sentence here. */
const nothingTriggersReview = "Nothing on this order triggers internal review.";

/**
 * The second half of that sentence, which the approved design writes for a draft. A locked order
 * has already been sold, cancelled or expired, so offering to send it reads oddly on the record.
 */
const canGoToTheCustomer = "It can go straight to the customer.";

/**
 * The advisory group's statement. It says what the flag does not do, because the flag's name invites
 * the opposite reading and a representative who believes the order is blocked stops working on it.
 */
const advisoryStatement = "Flagged for review. This is advisory only — it does not block publishing or acceptance.";

/**
 * The staff confirmation's statement, and that hold's alone. Billing setup and provisioning, not
 * publishing: a held order can still be published to the customer and still be accepted by them.
 */
const gatingStatement = "Staff must confirm this order before billing setup or provisioning can start.";

/**
 * The gating group's statement once staff have released the hold. The order waits for nobody now,
 * so the warning above must not stand beside it.
 *
 * The actor is named where the record carries one. `OrderMapper.ToInternalConfirmationDto` sends
 * `confirmedByUser` as null on every real release today - the fulfilment step holds the actor and
 * no read reaches it - so the time alone has to carry the statement in the meantime.
 * @param hold
 */
function releasedStatement(hold: OrderInternalConfirmationDto): string {
  const when = hold.confirmedAt === null ? "an unrecorded time" : `${formatInstantInUtc(hold.confirmedAt)} UTC`;
  const released = hold.confirmedByUser
    ? `Confirmed by ${hold.confirmedByUser} at ${when}.`
    : `Confirmed at ${when}, by a staff member the record does not name.`;
  return `${released} Billing setup and provisioning can start.`;
}

/**
 * The order's own hold, said in the shape the server writes its gating reasons in. It is not one of
 * them: no rule stamps it, a representative sets it on this order, and clearing `Automatic
 * fulfilment` is the only thing that raises it. Its consequence is the group's, though - an
 * unapproved `ManualGate` step stops billing setup exactly as an unconfirmed hold does - so the
 * section states it here rather than leaving the reader with a checkbox two sections away.
 *
 * The row states the condition alone. What the condition means right now - a wait, or an approval
 * already given - is the statement's job, because the order's own field is frozen at acceptance
 * and cannot see the approval.
 */
export const manualFulfilmentHoldReason = "Automatic fulfilment is off for this order.";

/**
 * The manual gate's statement while it still holds the order. It is a different hold from the
 * staff confirmation, and the section stated the confirmation's sentence for both: the card read
 * "Staff must confirm this order" over an order whose confirmation was never required.
 */
export const manualFulfilmentStatement =
  "A staff member must approve fulfilment before billing setup or provisioning can start.";

/** The manual gate's statement once staff have approved it on the fulfilment record. */
export const manualFulfilmentApprovedStatement =
  "A staff member approved fulfilment. Billing setup and provisioning can start.";

const advisoryHeading = "Advisory review";
const gatingHeading = "Staff confirmation hold";
/** The group's heading where the manual gate is the only hold on the order. */
export const manualFulfilmentHeading = "Fulfilment approval hold";

/**
 * One reason, as a row a reader scans. Not a checkbox: an advisory tick would have nowhere to be
 * stored, and a checkbox that forgets what was ticked is worse than no checkbox. The gating hold is
 * released by staff through the fulfilment step, and the switch below is the only control here that
 * writes anything.
 * @param root0
 * @param root0.reason
 * @param root0.accent
 * @param root0.icon
 */
function ReasonRow({
  reason,
  accent,
  icon,
}: {
  reason: string;
  accent: "warning.main" | "info.main";
  icon: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: "flex-start",
        border: 1,
        borderColor: "divider",
        borderLeft: 4,
        borderLeftColor: accent,
        borderRadius: 1,
        p: 1.25,
      }}
    >
      <Box sx={{ color: accent, display: "flex", flex: "none" }}>{icon}</Box>
      <Typography variant="body2">{reason}</Typography>
    </Stack>
  );
}

/**
 * One of the two groups: its heading, its statement, and its reasons. The groups are separate cards
 * because their consequences are different, and a single list would let an advisory item read as
 * something that blocks work.
 * @param root0
 * @param root0.heading
 * @param root0.testId
 * @param root0.children
 */
function ReviewGroup({ heading, testId, children }: { heading: string; testId: string; children: React.ReactNode }) {
  return (
    // A bordered box rather than a card: the section around it is already the card, and the portal
    // groups a block inside one this way - see the tax-exemption box on the billing account.
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }} data-testid={testId}>
      <Stack spacing={1.5}>
        <Typography variant="body2" component="h3" sx={{ color: "text.secondary", ...detailFieldLabelSx }}>
          {heading}
        </Typography>
        {children}
      </Stack>
    </Box>
  );
}

/**
 * The hold as a reader sees it when they cannot change it. A released hold says so: it was required
 * once, and reading "Required before billing setup can start" on a cleared gate is what OQF-12
 * rules out.
 * @param root0
 * @param root0.hold
 * @param root0.isReleased
 */
function HoldReadOnly({ hold, isReleased }: { hold: OrderInternalConfirmationDto; isReleased: boolean }) {
  const state = isReleased ? "Released" : hold.isRequired ? "Required before billing setup can start" : "Not required";
  return (
    <Box>
      {/* Named, because the manual fulfilment gate is stated in the same group. A bare "Not
          required" under a hold that does say it applies reads as a contradiction. */}
      <Typography variant="body2">{`Staff confirmation: ${state}`}</Typography>
      {hold.note && (
        <Typography variant="body2" color="text.secondary">
          {hold.note}
        </Typography>
      )}
    </Box>
  );
}

/**
 * True where staff have approved the manual gate on the record.
 * @param fulfillment
 */
function isManualGateComplete(fulfillment?: OrderFulfillmentDto): boolean {
  return (fulfillment?.steps ?? []).some((step) => step.kind === FulfillmentStepKind.ManualGate && step.isComplete);
}

/**
 * Why an order is under internal review, in two groups that do different things.
 *
 * `reviewReasons` is advisory and blocks nothing. `internalConfirmationReasons` is a hold, and it
 * gates billing setup and provisioning - not publishing and not acceptance. The approved prototype
 * modelled the two as one blocking checklist and told the reader the order could not be sent until
 * every item was ticked; that sentence is false here, so it is not reproduced and the groups are
 * rendered apart instead.
 *
 * The order's own manual-fulfilment hold joins the gating group - see
 * {@link manualFulfilmentHoldReason} - because this section is where the order says what holds it.
 * It keeps its own statement: it is a second hold, not a second reason for the first one, and only
 * the fulfilment record can say whether staff have approved it.
 * @param root0
 * @param root0.order
 * @param root0.draft
 * @param root0.readOnly
 * @param root0.fulfillment
 * @param root0.onOrderChanged
 */
export function InternalReviewSection({
  order,
  draft,
  readOnly,
  fulfillment,
  onOrderChanged,
}: SectionProps & {
  /** The fulfilment record, where the manual gate's own state is kept. */
  fulfillment?: OrderFulfillmentDto;
  /** Where the hold's own write reports the record it produced. */
  onOrderChanged: (order: OrderDto) => void;
}) {
  const [savedHold, setSavedHold] = React.useState(order.internalConfirmation);
  const [isRequired, setIsRequired] = React.useState(savedHold.isRequired);
  const [note, setNote] = React.useState(savedHold.note ?? "");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<unknown>(undefined);

  const advisoryReasons = order.reviewReasons;
  // The draft rather than the saved order: the representative reads both sections on the one page,
  // so the hold must appear the moment the checkbox is cleared.
  const manualFulfilmentApplies = draft.requiresManualFulfillmentApproval;
  const gatingReasons = manualFulfilmentApplies
    ? [...order.internalConfirmationReasons, manualFulfilmentHoldReason]
    : order.internalConfirmationReasons;
  // A rule that fired and a hold a staff member set are the same gate: the reasons are derived
  // from the rules that stamp the hold, so either one standing means the confirmation applies.
  const confirmationApplies = order.internalConfirmationReasons.length > 0 || savedHold.isRequired;
  const gatingApplies = confirmationApplies || manualFulfilmentApplies;
  // Staff have cleared the gate. The hold is still required - that input is frozen at acceptance -
  // so only the state says the order has stopped waiting.
  const isReleased = savedHold.state === InternalConfirmationState.Confirmed;
  // The order's own field says only that the gate was applied at acceptance. Whether a staff
  // member has since approved it is kept on the record, and nowhere else.
  const isFulfilmentApproved = isManualGateComplete(fulfillment);
  // The switch below is the staff confirmation's own control, so the group answers for that hold
  // wherever it is offered. A reader who cannot change anything is told which hold is on the
  // order instead.
  const heading = confirmationApplies || !readOnly ? gatingHeading : manualFulfilmentHeading;
  const nothingFires = advisoryReasons.length === 0 && !gatingApplies;
  const isHoldDirty = isRequired !== savedHold.isRequired || note !== (savedHold.note ?? "");

  const saveHold = async () => {
    setSaving(true);
    setError(undefined);
    try {
      // Clearing the hold drops its note with it: the API refuses a note sent alongside a cleared
      // hold rather than ignoring it, so the note travels only while the hold still stands.
      const saved = await setInternalConfirmation(order.odNumber, {
        requiresInternalConfirmation: isRequired,
        note: isRequired ? note.trim() || null : null,
      });
      setSavedHold(saved.internalConfirmation);
      setNote(saved.internalConfirmation.note ?? "");
      onOrderChanged(saved);
    } catch (saveError) {
      setError(saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BuilderSection title="Internal review">
      {nothingFires && (
        <Typography variant="body2" color="text.secondary">
          {isOrderLocked(order.status) ? nothingTriggersReview : `${nothingTriggersReview} ${canGoToTheCustomer}`}
        </Typography>
      )}

      {advisoryReasons.length > 0 && (
        <ReviewGroup heading={advisoryHeading} testId="advisory-review-group">
          <Alert severity="warning">{advisoryStatement}</Alert>
          <Stack spacing={1}>
            {advisoryReasons.map((reason) => (
              <ReasonRow
                key={reason}
                reason={reason}
                accent="warning.main"
                icon={<WarningAmberIcon fontSize="small" />}
              />
            ))}
          </Stack>
        </ReviewGroup>
      )}

      {(gatingApplies || !readOnly) && (
        <ReviewGroup heading={heading} testId="gating-review-group">
          {isReleased ? (
            <Alert severity="success">{releasedStatement(savedHold)}</Alert>
          ) : (
            confirmationApplies && <Alert severity="info">{gatingStatement}</Alert>
          )}
          {manualFulfilmentApplies &&
            (isFulfilmentApproved ? (
              <Alert severity="success">{manualFulfilmentApprovedStatement}</Alert>
            ) : (
              <Alert severity="info">{manualFulfilmentStatement}</Alert>
            ))}
          {gatingReasons.length > 0 && (
            <Stack spacing={1}>
              {gatingReasons.map((reason) => (
                <ReasonRow
                  key={reason}
                  reason={reason}
                  accent="info.main"
                  icon={<LockOutlinedIcon fontSize="small" />}
                />
              ))}
            </Stack>
          )}

          {readOnly ? (
            <HoldReadOnly hold={savedHold} isReleased={isReleased} />
          ) : (
            <Stack spacing={1}>
              {!!error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
              <FormControlLabel
                control={
                  <Switch
                    checked={isRequired}
                    onChange={(event) => setIsRequired(event.target.checked)}
                    slotProps={{ input: { "aria-label": "Requires internal confirmation" } }}
                  />
                }
                label="Require staff confirmation"
                slotProps={{ typography: { variant: "body2" } }}
              />
              {isRequired && (
                <TextField
                  label="Note for the confirming staff member"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
              )}
              <Box>
                <Button
                  variant="outlined"
                  disabled={!isHoldDirty || saving}
                  loading={saving}
                  onClick={() => void saveHold()}
                >
                  Save
                </Button>
              </Box>
            </Stack>
          )}
        </ReviewGroup>
      )}
    </BuilderSection>
  );
}
